import React, { createContext, useState, useEffect, useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import { setAuthCallbacks } from '../core/api'

const AuthContext = createContext()

// Where to send unauthenticated users. Override via .env if staffing-app
// moves domains.
const STAFFING_APP_URL =
  import.meta.env.VITE_STAFFING_APP_URL || 'https://www.staffing.edc.design'

// Only the staffing-app may hand ShortCircuit an auth token.
//
// Both listeners below used to accept EDC_AUTH / EDC_AUTH_REFRESH from ANY
// sender and write the token straight into state and localStorage. Any page
// able to reach this window could therefore inject a session, or resolve an
// in-flight refresh with a token of its choosing. The trust was one-sided:
// the parent already does the mirror-image check in staffing-app's
// ShortCircuitEmbed ("if (event.origin !== SC_ORIGIN) return").
//
// Compared as an origin rather than a raw string, so a trailing slash or path
// in VITE_STAFFING_APP_URL cannot silently break the match.
const STAFFING_APP_ORIGIN = (() => {
  try {
    return new URL(STAFFING_APP_URL).origin
  } catch {
    return STAFFING_APP_URL
  }
})()

const isTrustedAuthOrigin = (event) => event.origin === STAFFING_APP_ORIGIN

// How long to wait for a token via postMessage before giving up and
// redirecting to the staffing-app login page.
const TOKEN_WAIT_MS = 4000

// Decode the JWT payload without verifying — verification happens
// server-side in the Authorizer Lambda, the client just needs the claims
// for identity (userId, email, name). Manual base64 + JSON to avoid
// pulling in jwt-decode as a runtime dep.
const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null
  const segments = token.split('.')
  if (segments.length !== 3) return null
  try {
    // JWT uses base64url; convert to base64 then decode.
    const b64 = segments[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch (_) {
    return null
  }
}

// Merge JWT claims into the postMessage user object so callers can rely
// on `user.id` / `user.userId` being present even if the parent frame
// didn't include it. EdcAuthLambda's `_jwt.js` puts `userId` in the
// access token payload, so the JWT is the canonical source for ids.
const mergeUserFromToken = (token, userData) => {
  const claims = decodeJwtPayload(token)
  const tokenUser = claims
    ? {
        id: claims.userId || claims.sub || null,
        userId: claims.userId || claims.sub || null,
        email: claims.email || null,
        name: claims.name || null,
        firstName: claims.firstName || null,
        lastName: claims.lastName || null,
        appRoleId: claims.appRoleId || null,
      }
    : {}
  return { ...tokenUser, ...(userData || {}), id: (userData && userData.id) || tokenUser.id, userId: (userData && userData.userId) || (userData && userData.id) || tokenUser.userId }
}

export const AuthProvider = ({ children }) => {
  const [idToken, setIdToken] = useState(null)
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const timeoutRef = useRef(null)

  const applyToken = (token, userData) => {
    const merged = mergeUserFromToken(token, userData)
    setIdToken(token)
    setUser(merged)
    setIsLoading(false)
    // Mirror into localStorage so same-origin reloads skip the handshake.
    localStorage.setItem('edc_access_token', token)
    localStorage.setItem('edc_user', JSON.stringify(merged))
  }

  useEffect(() => {
    const inIframe = window.parent !== window
    const allowStandalone = import.meta.env.VITE_ALLOW_STANDALONE === 'true'

    // 1. Standalone visit (user opened dev.shortcircuit.edc.design directly).
    //    SC is meant to launch via the staffing-app hub so the AppLauncher
    //    can mint a fresh iframe session. A leftover localStorage token from
    //    a prior iframe mount would let the user bypass the hub forever, so
    //    clear it and bounce to the hub. The hub will re-embed SC and pass
    //    a fresh token via postMessage.
    if (!inIframe && !allowStandalone) {
      localStorage.removeItem('edc_access_token')
      localStorage.removeItem('edc_refresh_token')
      localStorage.removeItem('edc_user')
      window.location.href = `${STAFFING_APP_URL}/shortcircuit`
      return
    }

    // 2. Inside an iframe: skip the postMessage handshake if a same-origin
    //    token from the previous mount is still in localStorage.
    const stored = localStorage.getItem('edc_access_token')
    if (stored) {
      const storedUser = localStorage.getItem('edc_user')
      applyToken(stored, storedUser ? JSON.parse(storedUser) : null)
      return
    }

    // 3. Cross-origin iframe: wait for parent to postMessage the token.
    const handleMessage = (event) => {
      if (!isTrustedAuthOrigin(event)) return
      if (!event.data?.type) return

      if (event.data.type === 'EDC_AUTH' && event.data.token) {
        clearTimeout(timeoutRef.current)
        applyToken(event.data.token, event.data.user)
      }

      if (event.data.type === 'EDC_AUTH_REFRESH' && event.data.token) {
        setIdToken(event.data.token)
        localStorage.setItem('edc_access_token', event.data.token)
      }

      if (event.data.type === 'EDC_LOGOUT') {
        handleLogout()
      }
    }

    window.addEventListener('message', handleMessage)

    // Ask parent for the token immediately (staffing-app ShortCircuitEmbed
    // listens for this and responds with EDC_AUTH).
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'SC_REQUEST_AUTH' }, STAFFING_APP_URL)
    }

    // 4. Timeout — nothing arrived; bounce to staffing-app login.
    // returnTo is the staffing-app's iframe route (/shortcircuit), NOT the
    // SC URL itself — cross-origin localStorage means SC can never read a
    // token set by staffing-app, so sending users back here would loop.
    // The iframe route receives the token via postMessage after login.
    //
    // IMPORTANT: redirect window.top, NOT window — when SC is loaded
    // inside the staffing-app's iframe, redirecting `window.location`
    // navigates only the iframe. The staffing-app then renders its
    // /shortcircuit route inside the iframe → another SC iframe →
    // another timeout → another redirect, stacking a "Back to Hub"
    // bar at every recursion level. Escaping to window.top makes the
    // whole browser go to the login page once, instead.
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false)
      const target = `${STAFFING_APP_URL}/login?returnTo=/shortcircuit`
      try {
        // Cross-origin parents block reads of `top.location`, but
        // assignment is allowed by the same-origin policy as long as
        // the assigned URL is well-formed.
        if (window.top && window.top !== window.self) {
          window.top.location.href = target
          return
        }
      } catch (_) {
        // Fall through to in-frame redirect on the rare browsers that
        // disallow the assignment.
      }
      window.location.href = target
    }, TOKEN_WAIT_MS)

    return () => {
      window.removeEventListener('message', handleMessage)
      clearTimeout(timeoutRef.current)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Same window.top / fallback pattern used for the timeout redirect:
  // ensures logout / login-redirect navigates the whole browser, not
  // just the SC iframe (which would re-mount staffing-app inside SC).
  const navigateTopLevel = (url) => {
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = url
        return
      }
    } catch (_) { /* fall through */ }
    window.location.href = url
  }

  const handleLogout = () => {
    setIdToken(null)
    setUser(null)
    localStorage.removeItem('edc_access_token')
    localStorage.removeItem('edc_refresh_token')
    localStorage.removeItem('edc_user')
    navigateTopLevel(STAFFING_APP_URL)
  }

  // How long to wait for a parent-issued refreshed token before giving up.
  const REFRESH_WAIT_MS = 3000

  const refreshToken = () =>
    new Promise((resolve, reject) => {
      // Standalone (no parent frame) has no refresh source — fail fast so
      // the api.jsx retry path bails out cleanly rather than retrying with
      // the stale token in a loop.
      if (window.parent === window) {
        reject(new Error('No parent frame available to issue a refreshed token'))
        return
      }

      let settled = false
      const cleanup = () => {
        window.removeEventListener('message', onMessage)
        clearTimeout(timer)
      }

      const onMessage = (event) => {
        if (!isTrustedAuthOrigin(event)) return
        // Accept BOTH message types: the staffing-app's ShortCircuitEmbed
        // currently posts the same `EDC_AUTH` envelope for the initial
        // handshake and for refresh responses (its sendAuth() doesn't
        // discriminate). Restricting to only EDC_AUTH_REFRESH meant the
        // refresh promise never resolved, the 3s timeout fired, and we
        // cascaded into a fake "refresh failed → logout" sequence even
        // though the parent did hand us a fresh token.
        if (
          event.data?.type !== 'EDC_AUTH' &&
          event.data?.type !== 'EDC_AUTH_REFRESH'
        ) return
        if (!event.data.token) return
        if (settled) return
        settled = true
        cleanup()
        // Mirror the new token into state + localStorage immediately so the
        // next request after the retry uses the fresh credentials. The
        // always-on handler also writes these — both paths are idempotent.
        setIdToken(event.data.token)
        localStorage.setItem('edc_access_token', event.data.token)
        resolve(event.data.token)
      }

      window.addEventListener('message', onMessage)

      const timer = setTimeout(() => {
        if (settled) return
        settled = true
        cleanup()
        reject(new Error('Token refresh timed out'))
      }, REFRESH_WAIT_MS)

      window.parent.postMessage({ type: 'SC_REQUEST_REFRESH' }, STAFFING_APP_URL)
    })

  // Keep api.jsx callbacks in sync whenever the token changes.
  useEffect(() => {
    setAuthCallbacks(refreshToken, handleLogout)
  }, [idToken]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!idToken,
        user,
        userId: user?.id || user?.userId || null,
        isLoading,
        idToken,
        refreshToken,
        logout: handleLogout,
        loginWithRedirect: () => {
          navigateTopLevel(`${STAFFING_APP_URL}/login`)
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useAuth = () => useContext(AuthContext)
